import apiClient from '../../../../shared/api/apiClient'
import { ENDPOINTS } from '../../../../shared/api/endpoints'
import { extractErrorMessage } from '../../../../shared/utils/errorUtils'
import type {
  CreateLeavePolicyPayload,
  LeavePoliciesQueryParams,
  LeavePolicyDto,
  StageAttachmentResponse,
  UpdateLeavePolicyPayload,
} from '../types/hrmsLeavePolicies.types'

/**
 * Fetch Leave Policies.
 * Optional query: staffTypeId, status (ACTIVE|INACTIVE). Omit empty params.
 */
export const getLeavePolicies = async (
  params?: LeavePoliciesQueryParams
): Promise<LeavePolicyDto[]> => {
  const queryParams: Record<string, string> = {}
  if (params?.staffTypeId && params.staffTypeId !== 'All') {
    queryParams.staffTypeId = params.staffTypeId
  }
  if (params?.status) {
    queryParams.status = params.status
  }

  const { data } = await apiClient.get<LeavePolicyDto[] | { data: LeavePolicyDto[] }>(
    ENDPOINTS.HRMS.LEAVE_POLICIES,
    {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    }
  )

  const raw = (data as any)?.data ?? data
  if (raw && typeof raw === 'object' && 'statusCode' in raw && 'error' in raw && !Array.isArray(raw)) {
    throw new Error(extractErrorMessage(raw, 'Failed to fetch leave policies'))
  }
  return Array.isArray(raw) ? raw : []
}

/**
 * Fetch a single Leave Policy by ID.
 */
export const getLeavePolicyById = async (id: string): Promise<LeavePolicyDto> => {
  const { data } = await apiClient.get<LeavePolicyDto | { data: LeavePolicyDto }>(
    ENDPOINTS.HRMS.LEAVE_POLICY_BY_ID(id)
  )

  const payload = (data as any)?.data ?? data
  if (
    payload &&
    typeof payload === 'object' &&
    'statusCode' in payload &&
    'error' in payload &&
    !('id' in payload)
  ) {
    throw new Error(extractErrorMessage(payload, 'Failed to fetch policy details'))
  }

  return payload
}

/**
 * Stage an attachment file before creating or updating a leave policy.
 * Multipart/form-data with field "file".
 */
export const stageLeavePolicyAttachment = async (
  file: File
): Promise<StageAttachmentResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const { data } = await apiClient.post<StageAttachmentResponse | { data: StageAttachmentResponse }>(
    ENDPOINTS.HRMS.LEAVE_POLICY_ATTACHMENT_STAGE,
    formData
  )

  return (data as any)?.data ?? data
}

/**
 * Create a new leave policy.
 */
export const createLeavePolicy = async (
  payload: CreateLeavePolicyPayload
): Promise<LeavePolicyDto> => {
  const { data } = await apiClient.post<LeavePolicyDto | { data: LeavePolicyDto }>(
    ENDPOINTS.HRMS.LEAVE_POLICIES,
    payload
  )

  return (data as any)?.data ?? data
}

/**
 * Update an existing leave policy.
 * PATCH with editable fields only.
 */
export const updateLeavePolicy = async (
  id: string,
  payload: UpdateLeavePolicyPayload
): Promise<LeavePolicyDto> => {
  const { data } = await apiClient.patch<LeavePolicyDto | { data: LeavePolicyDto }>(
    ENDPOINTS.HRMS.LEAVE_POLICY_BY_ID(id),
    payload
  )

  return (data as any)?.data ?? data
}

export interface StaffTypeLookupItem {
  id: string
  valueCode?: string
  displayName?: string
  name?: string
  label?: string
}

/**
 * Fetch Staff Type lookup values for employee group options.
 * GET /api/lookup-values?typeCode=STAFF_TYPE
 */
export const getStaffTypeLookupValues = async (): Promise<StaffTypeLookupItem[]> => {
  const { data } = await apiClient.get<StaffTypeLookupItem[] | { data: StaffTypeLookupItem[] }>(
    ENDPOINTS.LOOKUP.VALUES,
    {
      params: { typeCode: 'STAFF_TYPE' },
    }
  )

  const raw = (data as any)?.data ?? data
  if (raw && typeof raw === 'object' && 'statusCode' in raw && 'error' in raw && !Array.isArray(raw)) {
    throw new Error(extractErrorMessage(raw, 'Failed to fetch staff types'))
  }
  return Array.isArray(raw) ? raw : []
}

// Aliases for convenience
export const fetchLeavePolicies = getLeavePolicies
export const fetchLeavePolicyById = getLeavePolicyById

export interface LeavePolicyDropdownItem {
  id: string
  name: string
  code?: string
  remainingDays?: number
}

/**
 * Fetch leave policies dropdown options.
 * GET /api/hrms/leave/policies/dropdown
 */
export const getLeavePoliciesDropdown = async (): Promise<LeavePolicyDropdownItem[]> => {
  const { data } = await apiClient.get<unknown>(ENDPOINTS.HRMS.LEAVE_POLICIES_DROPDOWN)
  const raw = (data as any)?.data ?? data
  const items = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.items)
    ? raw.items
    : []

  return items.map((item: any) => ({
    id: String(item.id || item.policyId || item.value || ''),
    name: String(item.name || item.policyName || item.label || 'Leave Policy'),
    code: item.code,
    remainingDays:
      typeof item.remainingDays === 'number'
        ? item.remainingDays
        : typeof item.availableDays === 'number'
        ? item.availableDays
        : typeof item.balance === 'number'
        ? item.balance
        : undefined,
  }))
}

